import { Base64 } from "js-base64";

/**
 * Parse URL parameters and extract event and referral information
 * @param {string} customPath - Optional custom path to parse instead of current URL
 * @returns {Object} Parsed URL data
 */
export const parseEventShareURL = (customPath = null) => {
  try {
    let sessionParam = null;
    let inviteParam = null;
    let originalURL = null;

    if (customPath) {
      // Parse custom path directly
      console.log('🔍 Parsing custom path:', customPath);
      const sessionMatch = customPath.match(/session=([^&?]+)/);
      const inviteMatch = customPath.match(/invite=([^&?]+)/);

      if (sessionMatch) {
        sessionParam = sessionMatch[1];
      }
      if (inviteMatch) {
        inviteParam = inviteMatch[1];
      }

      originalURL = `${window.location.origin}${customPath}`;
      console.log('🔧 Extracted from custom path - session:', !!sessionParam, 'invite:', !!inviteParam);
    } else {
      // Parse current URL
      const urlParams = new URLSearchParams(window.location.search);
      sessionParam = urlParams.get('session');
      inviteParam = urlParams.get('invite');
      originalURL = window.location.href;

      // Check if we're on Setup page with encoded path parameter
      if (window.location.pathname === '/Setup' && urlParams.has('path')) {
        const pathParam = urlParams.get('path');
        const decodedPath = decodeURIComponent(pathParam);
        console.log('🔍 Parsing Setup page path parameter:', decodedPath);

        // Check if this is a GameShow URL that got redirected to Setup
        if (decodedPath.includes('GameShow') || decodedPath.includes('eventID')) {
          console.log('🎮 Detected GameShow URL in Setup redirect, handling...');

          // Extract eventID from the path
          const eventIDMatch = decodedPath.match(/eventID=([^&?]+)/);
          const inviteMatch = decodedPath.match(/invite=([^&?]+)/);

          if (eventIDMatch) {
            const eventID = eventIDMatch[1];
            let redirectPath = `/events/game-show?eventID=${eventID}`;

            if (inviteMatch) {
              redirectPath += `&invite=${inviteMatch[1]}`;
            }

            console.log('🔄 Redirecting to:', redirectPath);
            // Force reload to the correct path
            window.location.replace(`${window.location.origin}${redirectPath}`);
            return {
              eventId: null,
              referralCode: null,
              directReferrerId: null,
              isEventShareURL: false,
              originalURL: null
            };
          }
        }

        // Handle malformed URLs with multiple ? symbols
        // Extract session parameter using regex
        const sessionMatch = decodedPath.match(/session=([^&?]+)/);
        const inviteMatch = decodedPath.match(/invite=([^&?]+)/);

        if (sessionMatch) {
          sessionParam = sessionMatch[1];
        }
        if (inviteMatch) {
          inviteParam = inviteMatch[1];
        }

        // Reconstruct the original URL
        originalURL = `${window.location.origin}${decodedPath}`;
        console.log('🔧 Extracted from path - session:', !!sessionParam, 'invite:', !!inviteParam);
      }
    }

    console.log('🔍 parseEventShareURL:', {
      url: window.location.href,
      customPath,
      originalURL,
      sessionParam,
      inviteParam
    });

    let eventId = null;
    let referralCode = null;
    let directReferrerId = null;

    // Parse session parameter to get eventId
    if (sessionParam) {
      console.log('🔍 Decoding session parameter:', sessionParam);
      try {
        const decodedSession = Base64.decode(sessionParam);
        console.log('🔓 Decoded session string:', decodedSession);

        const sessionData = JSON.parse(decodedSession);
        console.log('📊 Parsed session data:', sessionData);

        eventId = sessionData.eventId;
        console.log('🎯 Extracted eventId:', eventId);
      } catch (error) {
        console.error("❌ Error parsing session parameter:", error);
        console.log('🔍 Session param that failed:', sessionParam);
      }
    }

    // Parse invite parameter to get referral data
    if (inviteParam) {
      console.log('🔍 Decoding invite parameter:', inviteParam);
      try {
        const decodedInvite = Base64.decode(inviteParam);
        console.log('🔓 Decoded invite string:', decodedInvite);

        const inviteData = JSON.parse(decodedInvite);
        console.log('📊 Parsed invite data:', inviteData);

        referralCode = inviteData.referralCode;
        directReferrerId = inviteData.directReferrerId;
        console.log('🎯 Extracted referral data:', { referralCode, directReferrerId });
      } catch (error) {
        console.error("❌ Error parsing invite parameter:", error);
        console.log('🔍 Invite param that failed:', inviteParam);
      }
    }

    return {
      eventId,
      referralCode,
      directReferrerId,
      isEventShareURL: !!eventId,
      originalURL
    };
  } catch (error) {
    console.error("Error parsing URL:", error);
    return {
      eventId: null,
      referralCode: null,
      directReferrerId: null,
      isEventShareURL: false,
      originalURL: null
    };
  }
};

/**
 * Store the event share URL in sessionStorage
 * @param {string} url - The URL to store
 */
export const storeEventShareURL = (url) => {
  try {
    sessionStorage.setItem('pendingEventUrl', url);
  } catch (error) {
    console.error("Error storing event URL:", error);
  }
};

/**
 * Retrieve the stored event share URL from sessionStorage
 * @returns {string|null} The stored URL or null if not found
 */
export const getStoredEventShareURL = () => {
  try {
    return sessionStorage.getItem('pendingEventUrl');
  } catch (error) {
    console.error("Error retrieving stored event URL:", error);
    return null;
  }
};

/**
 * Clear the stored event share URL from sessionStorage
 */
export const clearStoredEventShareURL = () => {
  try {
    sessionStorage.removeItem('pendingEventUrl');
  } catch (error) {
    console.error("Error clearing stored event URL:", error);
  }
};

/**
 * Extract event ID from URL if it exists
 * @param {string} pathParam - Optional path parameter to check
 * @returns {string|null} Event ID or null if not found
 */
export const getEventIdFromURL = (pathParam = null) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Try direct URL first
    let sessionParam = urlParams.get('session');
    console.log('🔍 Direct URL session param:', sessionParam);
    console.log('🔍 Current pathname:', window.location.pathname);

    // If not found, check for path parameter (from URL params or passed directly)
    if (!sessionParam) {
      console.log('🔍 Checking for path parameter');
      let pathToCheck = pathParam || urlParams.get('path');
      console.log('📍 Path to check:', pathToCheck);

      if (pathToCheck) {
        const decodedPath = decodeURIComponent(pathToCheck);
        console.log('📍 Decoded path:', decodedPath);

        const sessionMatch = decodedPath.match(/session=([^&?]+)/);
        console.log('🔍 Session match result:', sessionMatch);

        if (sessionMatch) {
          sessionParam = sessionMatch[1];
          console.log('✅ Extracted session from path:', sessionParam);
        }
      }
    }

    // Try to decode and extract eventId
    if (sessionParam) {
      console.log('🔍 Found session parameter:', sessionParam);
      try {
        const decodedSession = Base64.decode(sessionParam);
        console.log('🔓 Decoded session string:', decodedSession);

        const sessionData = JSON.parse(decodedSession);
        console.log('📊 Parsed session data:', sessionData);

        const eventId = sessionData.eventId;
        console.log('🎯 Extracted eventId:', eventId);

        return eventId || null;
      } catch (error) {
        console.error("❌ Error decoding session parameter:", error);
        console.log('🔍 Session param that failed:', sessionParam);
      }
    }

    return null;
  } catch (error) {
    console.error("Error extracting event ID from URL:", error);
    return null;
  }
};

/**
 * Check if current URL is an event share link
 * @returns {boolean} True if current URL contains event share parameters
 */
export const isEventShareURL = () => {
  // Check for direct eventID parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const directEventId = urlParams.get('eventID');

  // Check for encoded eventId
  const eventId = getEventIdFromURL();

  // Check if URL contains GameShow related paths
  const isGameShowUrl = window.location.pathname.includes('game-show') ||
    window.location.pathname.includes('GameShow') ||
    window.location.search.includes('eventID') ||
    window.location.href.includes('GameShowScreen');

  const hasEventId = !!(directEventId || eventId || isGameShowUrl);

  console.log('🔍 isEventShareURL check:', {
    directEventId,
    eventId,
    isGameShowUrl,
    hasEventId,
    url: window.location.href
  });

  return hasEventId;
};

/**
 * Store the user's event share action choice in localStorage
 * @param {string} action - The action the user chose (accept, reject, negotiate)
 */
export const storeEventShareAction = (action) => {
  try {
    localStorage.setItem('eventShareAction', action);
    console.log('💾 Stored event share action:', action);
  } catch (error) {
    console.error('❌ Error storing event share action:', error);
  }
};

/**
 * Retrieve the stored event share action from localStorage
 * @returns {string|null} The stored action or null if not found
 */
export const getStoredEventShareAction = () => {
  try {
    const action = localStorage.getItem('eventShareAction');
    console.log('📤 Retrieved event share action:', action);
    return action;
  } catch (error) {
    console.error('❌ Error retrieving event share action:', error);
    return null;
  }
};

/**
 * Clear the stored event share action from localStorage
 */
export const clearStoredEventShareAction = () => {
  try {
    localStorage.removeItem('eventShareAction');
    console.log('🗑️ Cleared stored event share action');
  } catch (error) {
    console.error('❌ Error clearing event share action:', error);
  }
};

/**
 * Store pending challenge accept context before token purchase
 * This allows the app to resume the challenge accept flow after payment
 * @param {string} challengeID - The challenge ID being accepted
 * @param {string} eventID - Optional event ID associated with the challenge
 * @param {boolean} needsBeltPurchase - Whether user needs to purchase belts first (default true)
 */
export const storePendingChallengeAccept = (challengeID, eventID = null, needsBeltPurchase = true) => {
  try {
    const data = {
      challengeID,
      eventID,
      needsBeltPurchase, // Track if belt purchase modal should open first
      timestamp: Date.now()
    };
    sessionStorage.setItem('pendingChallengeAccept', JSON.stringify(data));
    console.log('💾 Stored pending challenge accept:', data);
  } catch (error) {
    console.error('❌ Error storing pending challenge accept:', error);
  }
};

/**
 * Get pending challenge accept context
 * Returns null if no pending challenge or if context has expired (30 minutes)
 * @returns {Object|null} The stored challenge context or null
 */
export const getPendingChallengeAccept = () => {
  try {
    const stored = sessionStorage.getItem('pendingChallengeAccept');
    if (stored) {
      const data = JSON.parse(stored);
      // Expire after 30 minutes
      if (Date.now() - data.timestamp < 30 * 60 * 1000) {
        console.log('📤 Retrieved pending challenge accept:', data);
        return data;
      }
      console.log('⏰ Pending challenge accept expired, clearing...');
      clearPendingChallengeAccept();
    }
    return null;
  } catch (error) {
    console.error('❌ Error retrieving pending challenge accept:', error);
    return null;
  }
};

/**
 * Clear pending challenge accept context
 */
export const clearPendingChallengeAccept = () => {
  try {
    sessionStorage.removeItem('pendingChallengeAccept');
    console.log('🗑️ Cleared pending challenge accept');
  } catch (error) {
    console.error('❌ Error clearing pending challenge accept:', error);
  }
};